import Color from 'color';

const blackColor = Color.hsl('#000000');
const primaryColor = Color.hsl('#1747FF');
const primaryHoverColor = Color.hsl('#004EFF');

export const theme = {
  palettes: {
    primary: primaryColor.toString(),
    primary90: primaryColor.fade(0.1).toString(),
    primary80: primaryColor.fade(0.2).toString(),
    primaryHover: primaryHoverColor.fade(0.7).toString(),
    primaryText: '#070707',
    primaryBackground: '#FAFAFA',
    black: blackColor.toString(),
    black85: blackColor.fade(0.15).toString(),
    black60: blackColor.fade(0.4).toString(),
    black50: blackColor.fade(0.5).toString(),
    black45: blackColor.fade(0.55).toString(),
    black40: blackColor.fade(0.6).toString(),
    black30: blackColor.fade(0.7).toString(),
    black20: blackColor.fade(0.8).toString(),
    border: '#E5E5E5',
    white: '#FFFFFF',
    orange: '#FF6800',
    yellow: '#FFC92C',
    red: '#FF0000',
    pink: '#F00A7E',
    darkGrey: '#656565',
    grey: '#BEBEBE',
    grey97: '#00000008',
    lightGrayWhite: '#F0F2F5',
    pastelRed: '#FF8583',
    pastelBlue: '#008BDB',
    pastelGreen: '#17BFA5',
    pastelYellow: '#FFB12A',
    pickup: '#FFC92C',
    delivery: '#FE1918',
    lightBlueGray: '#E8EDFF',
    lightYellow: '#FBC02D',
    blue: '#004EFF',
    status: {
      created: '#4972FE',
      inProgress: '#7C4DFF',
      assigned: '#20BEAC',
      changeDriver: '#20BEAC',
      findingDriver: '#7C4DFF',
      findingNewDriver: '#7C4DFF',
      pickingUp: '#FBC02D',
      pickedUp: '#20BEAC',
      delivering: '#FBC02D',
      delivered: '#20BEAC',
      noDriver: '#F44336',
      changePickUpTime: '#4972FE',
      rejected: '#B0BEC5',
      cancelled: '#B0BEC5',
      failed: '#B0BEC5',
      received: '#4972FE',
      partiallyDelivered: '#20BEAC',
      unsuccessfullyDelivered: '#B0BEC5',
      driverRejected: '#B0BEC5',
      returnOrder: '#FBC02D',
    },
    documentsStatus: {
      draft: '#F5F5F5',
      waitingApprove: '#F0F5FF',
      approved: '#F6FFED',
      rejected: '#FFF1F0',
      expired: '#FFF1F0',
      wait: '#E3EAF8',
      confirm: '#FFF1F0',
    },
    documentsStatusText: {
      draft: '#8C8C8C',
      waitingApprove: '#2F54EB',
      approved: '#52C41A',
      rejected: '#A60000',
      wait: '#2A58A6',
      confirm: '#CE6A06',
    },
    documentStatus: {
      required: '#E7E7E7',
      pending: '#F0F0F0',
      submitted: '#FEEDDC',
      rejected: '#FFF1F0',
      approved: '#F6FFED',
      expired: '#FFF1F0',
      wait: '#E3EAF8',
      confirm: '#FEEDDC',
    },
    documentStatusText: {
      required: '#434343',
      pending: blackColor.fade(0.75).toString(),
      submitted: '#CE6A06',
      rejected: '#A60000',
      approved: '#52C41A',
      expired: '#FF4D4F',
      wait: '#2A58A6',
      confirm: '#CE6A06',
    },
    alert: {
      info: {
        border: '#91D5FF',
        background: '#E6F7FF',
        icon: '#1689f4',
      },
      error: {
        border: '#FFCCC7',
        background: '#FFF1F0',
        icon: '#FF4d50',
      },
      warning: {
        border: '#FFE58F',
        background: '#FFFBE6',
        icon: '#FBAD13',
      },
    },
    blackTextBold: '#222222',
    blackTextNormal: '#333333',
    disabledText: '#BBBBBB',
    whiteSmoke: '#F2F2F2',
    gray40: '#666666',
    lightViolet: '#7C4DFF',
    lightIndigo: '#4972FE',
    heather: '#B0BEC5',
    phoneText: '#666666',
    busyText: '#B0BEC5',
    redCircle: '#e80000',
  },
  border: {
    whitePrimary: '#F2F2F2',
  },
  fonts: {
    SFProDisplayBold: 'SF Pro Display Bold',
    SFProDisplayRegular: 'SF Pro Display Regular',
    SFProTextSemiBold: 'SF Pro Text Semibold',
    SFProTextRegular: 'SF Pro Text Regular',
    SFProTextMedium: 'SF Pro Text Medium',
    SFProTextBold: 'SF Pro Text Bold',
    NotoSands: 'Noto Sans',
    NotoSansTh: 'Noto Sans Thai',
  },
  fontSize: {
    xxxxs: '0.625rem', // 10 px
    xxxs: '0.75rem', // 12 px
    xxs: '0.8125rem', // 13 px
    xs: '0.875rem', // 14 px
    sm: '0.9375rem', // 15 px
    md: '1rem', // 16 px
    lg: '1.125rem', // 18 px
    xl: '1.25rem', // 20 px
    xxl: '1.5rem', // 24 px
    xxxl: '2.25rem', // 36 px
  },
  fontWeight: {
    regular: 400,
    medium: 500,
    bold: 700,
  },
  spacing: {
    xxxs: '1px',
    xxs: '2px',
    xs: '5px',
    sm: '10px',
    md: '15px',
    lg: '20px',
    xl: '25px',
    xxl: '30px',
    xxxl: '50px',
    xxxxl: '80px',
  },
  elementSize: {
    navbarDividerHeight: '30px',
    headerHeight: '50px',
    filterPanelHeight: '80px',
    navbarDriverPortalHeight: '64px',
    paginationHeight: '50px',
    tabHeight: '50px',
    tabPanelWidth: '160px',
    tabNotifyButtonWidth: '30px',
    tabNotifyButtonHeight: '25px',
    tabDividerHeight: '25px',
    siderWidth: '50px',
    subNavHeight: '50px',
    previewImageMaxWidth: '100%',
    previewImageMaxHeight: '100%',
    tagBorderRadius: '25px',
  },
  device: {
    mobileS: `(max-width: ${'320px'})`,
    mobileM: `(max-width: ${'375px'})`,
    mobileL: `(max-width: ${'425px'})`,
    tablet: `(max-width: ${'768px'})`,
    laptop: `(max-width: ${'1024px'})`,
    laptopL: `(max-width: ${'1440px'})`,
    desktop: `(max-width: ${'2560px'})`,
    desktopL: `(max-width: ${'2560px'})`,
  },
  zIndices: {
    infoWindowsMarker: 10,
  },
};

type Theme = typeof theme;

export interface ThemeProps {
  theme: Theme;
}
